import type { ReactNode } from 'react'

/**
 * Een telling als eigen klein vak naast het woord, en niet als deel ervan (besluit van 1 november 2026).
 *
 * Aanleiding was "Voorwaarden 2" in de configurator, waar de 2 bij het woord leek te horen. Dezelfde
 * vorm geldt sindsdien overal waar een aantal naast een label staat: op een knop, een tab, een filter
 * of een kop. Eén vorm in de renderer, en de configurator gebruikt hem ook (hij laadt `schil.css` al).
 * De stijl staat bij `.ef-telling` in `schil.css`.
 *
 * Een aantal dat deel is van een zin ("1 van 3", "Nog 2 punten voordat u kunt indienen", "2 open
 * opmerkingen") blijft tekst: daar zegt de zin al wat het getal is.
 *
 * **Het vak krijgt geen eigen woorden voor de schermlezer.** Het element dat het vak draagt krijgt een
 * naam met `countName`: het zichtbare label voorop (WCAG 2.5.3), dan een dubbele punt en het aantal als
 * woorden ("Opmerkingen: 2 open"). Een verborgen tekstdeel in het vak zou in Chromium, absoluut
 * gepositioneerd, een spatie voor de komma zetten ("Overzicht , 2 opmerkingen"), en zonder woorden zou
 * het vak als los getal worden voorgelezen ("Controles 2").
 */
export type CountKind = 'waarschuwing' | 'fout'

export interface CountBadgeProps {
  /** Zonder soort het neutrale aantal; `waarschuwing` voor open punten, `fout` voor wat gebroken is. */
  readonly soort?: CountKind
  readonly children: ReactNode
}

export function CountBadge({ soort: kind, children }: CountBadgeProps): ReactNode {
  return (
    <span className="ef-telling" data-soort={kind}>
      {children}
    </span>
  )
}

/**
 * De toegankelijke naam van een label met een of meer tellingen: "Opmerkingen: 2 open",
 * "Controles: 2, 1 open punt". Lege delen vallen weg; zonder delen is het alleen het label. Spaties
 * worden genormaliseerd, zodat er nooit een dubbele spatie of een spatie voor een leesteken in staat.
 */
export function countName(label: string, parts: readonly (string | false | undefined)[]): string {
  const present = parts.map((part) => (typeof part === 'string' ? part.trim() : '')).filter((part) => part !== '')
  const name = present.length === 0 ? label : `${label.trim()}: ${present.join(', ')}`
  return name.replace(/\s+/g, ' ').trim()
}
